<script setup lang="ts">
import type { NovaSearchResultDetailUserProps } from './NovaSearchResultDetailUser.types'
import type { NovaRecyclerViewGird } from '@components/NovaRecyclerView/NovaRecyclerView.types'

withDefaults(defineProps<NovaSearchResultDetailUserProps>(), {
  compactMode: false,
})

const isInit = ref(false)
const isLoading = ref(false)
const isError = ref(false)
const searchStore = useSearchStore()
const containerRef = ref<Element | null>(null)
const grid = ref<NovaRecyclerViewGird>({
  itemsPerRow: 3,
  itemWidth: 100 + 6 + 6,
  itemHeight: 135 + 6 + 6,
})
const isMobile = ref(false)

// 최초 랜딩시 댓글 조회
onMounted(async () => {
  await handleOnFetchUsers(true)
  handleOnUpdateGrid()
  window.addEventListener('resize', handleOnUpdateGrid)
  isInit.value = true
})

onUnmounted(() => {
  searchStore.users = initSearchState.users
  window.removeEventListener('resize', handleOnUpdateGrid)
})

// 검색 키워드에 따라 사용자 목록 조회
watch(
  () => searchStore.keyword,
  () => {
    handleOnFetchUsers(true)
  }
)

const handleOnUpdateGrid = () => {
  const winWidth = window.innerWidth
  const listRect = containerRef.value?.getBoundingClientRect()

  // $mobile: 768px;
  isMobile.value = winWidth < 768

  grid.value = {
    ...grid.value,
    itemWidth: (listRect!.width + 6 + 6) / grid.value.itemsPerRow,
    itemHeight: isMobile.value ? 214 + 6 + 12 : 127 + 6 + 12,
  }
}

// 사용자 목록 조회
const handleOnFetchUsers = async (refresh = false) => {
  try {
    isLoading.value = true
    isError.value = false
    await searchStore.fetchSearchUsers(
      {
        ...searchStore.users?.payload,
        query: searchStore.keyword,
      },
      refresh
    )
  } catch {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <ClientOnly>
    <div :class="['users', { 'mode-compact': false }]">
      <div ref="containerRef" class="users-list">
        <NovaBoxEmptyMessage
          v-if="
            searchStore.users.refresh &&
            !searchStore.users?.items?.length &&
            !isError
          "
          :message="$t('statusMessages.users.empty')"
          :fill="true"
        />

        <NovaBoxEmptyMessage
          v-if="!searchStore.users?.items?.length && isError"
          :message="$t('statusMessages.users.error')"
          :fill="true"
        />

        <div class="search-result-hashtag">
          <NovaRecyclerGrid
            v-if="searchStore.users?.items?.length"
            :items="searchStore.users.items"
            :is-last="compactMode ? true : searchStore.users.last"
            :is-loading="isLoading"
            :is-error="isError"
            :is-refresh="searchStore.users.refresh"
            :key-name="'userSn'"
            :page-mode="true"
            :error-message="$t('statusMessages.users.error')"
            :responsive="{
              mobile: {
                columns: 3,
                rowGap: 12,
                columnGap: 12,
                padding: { left: 10, right: 10 },
              },
              tablet: { columns: 3, rowGap: 12, columnGap: 12 },
              desktop: { columns: 3, rowGap: 12, columnGap: 12 },
            }"
            :bottom-white-space="60"
            @load-more="handleOnFetchUsers"
          >
            <template #item="{ item }">
              <NovaBoxSearchUser :source="item" />
            </template>
          </NovaRecyclerGrid>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.users {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  &.mode-compact {
    @include mobile {
      .users-list {
        margin: 0 10px;
      }
    }
  }
}
</style>
